import { hasLocalToken } from '@frontend/features/auth/util/util.auth.localToken';

/**
 * Authentication mode for the application
 *
 * - 'e2e': E2E test mode - Uses token from localStorage (works on all environments)
 * - 'msal': MSAL mode - Uses Azure AD authentication via MSAL library
 */
export type AuthMode = 'e2e' | 'msal';

/**
 * Determines the current authentication mode based on localStorage token presence
 *
 * **E2E Mode** (when token exists in localStorage):
 * - Activated by visiting `/change-to-e2e` route or pasting token on localhost
 * - Works on all environments: localhost, dev, qa, prod
 * - Used by Playwright E2E tests to bypass MSAL authentication
 * - Used by developers on localhost for easier testing
 *
 * **MSAL Mode** (when no token in localStorage):
 * - Default mode on deployed environments (dev, qa, prod)
 * - Uses Azure AD authentication with MSAL library
 * - Requires user to log in via Microsoft login page
 *
 * @returns Current authentication mode
 *
 * @example
 * ```typescript
 * const mode = getAuthMode();
 * if (mode === 'e2e') {
 *   // Use token from localStorage
 * } else {
 *   // Use MSAL authentication
 * }
 * ```
 */
export function getAuthMode(): AuthMode {
  // Simple: if there's a token in localStorage, we're in E2E mode
  // This works on all environments (localhost, dev, qa, prod)
  return hasLocalToken() ? 'e2e' : 'msal';
}
